import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useApi } from '../api/api-context';
import { BoardThemePicker } from './BoardThemePicker';
import { ConnectedAccountsPanel } from './ConnectedAccountsPanel';
import { readLocalAnalysisPreference, writeLocalAnalysisPreference } from '../engine/preference';

export function ProfilePage(): JSX.Element {
  const api = useApi();
  const queryClient = useQueryClient();
  const profile = useQuery({ queryKey: ['profile'], queryFn: () => api.getProfile() });
  const [username, setUsername] = useState('');
  const [localAnalysis, setLocalAnalysis] = useState(() => readLocalAnalysisPreference());
  useEffect(() => { setUsername(profile.data?.username ?? ''); }, [profile.data?.username]);
  const save = useMutation({
    mutationFn: () => api.updateProfile(username.trim()),
    onSuccess: (data) => queryClient.setQueryData(['profile'], data),
  });

  return (
    <main className="profile-page">
      <Link to="/">← Repertoires</Link>
      <header className="profile-heading">
        <p className="eyebrow">YOUR ACCOUNT</p>
        <h1>Profile & settings</h1>
        <p>Manage your ItsAllPrep identity, connected chess accounts, board appearance and analysis preferences.</p>
      </header>
      <div className="profile-layout">
        <nav className="profile-nav" aria-label="Profile sections">
          <a href="#identity">Personal profile</a>
          <a href="#chess-accounts">Chess accounts & sync</a>
          <a href="#board-appearance">Board appearance</a>
          <a href="#analysis-preferences">Analysis preferences</a>
        </nav>
        <div className="profile-sections">
          <section id="identity" className="panel" aria-labelledby="identity-title">
            <h2 id="identity-title">Personal profile</h2>
            <p className="hint">Choose how you appear in ItsAllPrep. Your chess site usernames are managed separately below.</p>
            {profile.isPending ? <p role="status">Loading profile…</p> : profile.isError ? (
              <p role="alert">Could not load your profile. <button onClick={() => void profile.refetch()}>Try again</button></p>
            ) : (
              <form className="profile-form" onSubmit={(event) => { event.preventDefault(); save.mutate(); }}>
                <label htmlFor="profile-username">ItsAllPrep username</label>
                <input id="profile-username" autoComplete="username" required minLength={3} maxLength={24}
                  pattern="[a-zA-Z][a-zA-Z0-9_]{2,23}" value={username} disabled={save.isPending}
                  onChange={(event) => { setUsername(event.target.value); save.reset(); }} />
                <p className="hint">3–24 letters, numbers or underscores. Start with a letter.</p>
                <button className="studio-primary" disabled={save.isPending} type="submit">
                  {save.isPending ? 'Saving…' : 'Save profile'}
                </button>
                {save.isSuccess && <p role="status">Profile saved.</p>}
                {save.isError && <p role="alert">{save.error.message}</p>}
              </form>
            )}
          </section>
          <div id="chess-accounts"><ConnectedAccountsPanel /></div>
          <section id="board-appearance" className="panel" aria-labelledby="board-appearance-title">
            <h2 id="board-appearance-title">Board appearance</h2>
            <p className="hint">One skin for every board: the builder, practice and game review. Saved in this browser.</p>
            <BoardThemePicker />
          </section>
          <section id="analysis-preferences" className="panel" aria-labelledby="analysis-title">
            <h2 id="analysis-title">Analysis preferences</h2>
            <label className="profile-toggle">
              <input type="checkbox" checked={localAnalysis} onChange={(event) => {
                setLocalAnalysis(event.target.checked);
                writeLocalAnalysisPreference(event.target.checked);
              }} />
              Run local analysis as I browse
            </label>
            <p className="hint">Use this device to analyse positions automatically. Turn it off to reduce CPU and battery use. This preference is saved in this browser; local evaluations are advisory.</p>
          </section>
        </div>
      </div>
    </main>
  );
}
